import _styled, { default as _styled2, default as _styled3 } from "styled-components";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import "core-js/modules/es.regexp.exec.js";
import debounce from "lodash-es/debounce";
import React, { useCallback, useEffect, useRef } from "react";
import { forwardRef } from "react";
import styled, { useTheme } from "styled-components";
import Box, { BoxSpan } from "../../Styled/Box";
import { RawButton } from "../../Styled/Button";
import Icon, { StyledIcon } from "../../Styled/Icon";
import Text from "../../Styled/Text";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SearchInput = styled.input.withConfig({
  displayName: "SearchBox__SearchInput",
  componentId: "sc-9qbciu-0"
})(["box-sizing:border-box;margin-top:0;margin-bottom:0;border:none;border-radius:4px;height:40px;width:100%;display:block;padding:0.5rem 40px;vertical-align:middle;-webkit-appearance:none;"]);
export const DEBOUNCE_INTERVAL = 1000;
/**
 * Simple dumb search box component that leaves the actual execution of searches to the component that renders it. Note
 * that just like an input, this calls onSearchTextChanged when the value is changed, and expects that its parent
 * component will listen for this and update searchText with the new value.
 */
export const SearchBox = _ref => {
  let {
    onSearchTextChanged,
    onDoSearch,
    searchText,
    onFocus,
    placeholder = "Search",
    onClear,
    alwaysShowClear = false,
    debounceDuration,
    autoFocus = false,
    supportsAutocomplete = true,
    inputBoxRef
  } = _ref;
  const theme = useTheme();
  const debouncedOnDoSearch = useRef();
  useEffect(() => {
    const debounced = debounce(() => onDoSearch(), debounceDuration !== null && debounceDuration !== void 0 ? debounceDuration : DEBOUNCE_INTERVAL);
    debouncedOnDoSearch.current = debounced;
    return () => {
      debounced.flush();
    };
  }, [onDoSearch, debounceDuration]);
  useEffect(() => {
    return () => {
      var _debouncedOnDoSearch$;
      return (_debouncedOnDoSearch$ = debouncedOnDoSearch.current) === null || _debouncedOnDoSearch$ === void 0 ? void 0 : _debouncedOnDoSearch$.cancel();
    };
  }, []);
  const search = useCallback(() => {
    var _debouncedOnDoSearch$2;
    (_debouncedOnDoSearch$2 = debouncedOnDoSearch.current) === null || _debouncedOnDoSearch$2 === void 0 || _debouncedOnDoSearch$2.cancel();
    onDoSearch();
  }, [onDoSearch]);
  const handleChange = event => {
    const value = event.target.value;
    // immediately bypass debounce if we started with no value
    onSearchTextChanged(value);
    if (!supportsAutocomplete) return;
    if (searchText.length === 0) {
      search();
    } else {
      var _debouncedOnDoSearch$3;
      (_debouncedOnDoSearch$3 = debouncedOnDoSearch.current) === null || _debouncedOnDoSearch$3 === void 0 || _debouncedOnDoSearch$3.call(debouncedOnDoSearch);
    }
  };
  const clearSearch = () => {
    onSearchTextChanged("");
    search();
    if (onClear) {
      onClear();
    }
  };
  const onKeyDown = event => {
    if (event.key === "Enter") {
      search();
    }
  };
  const hasValue = searchText.length > 0;
  return /*#__PURE__*/_jsxs(_StyledForm, {
    autoComplete: "off",
    onSubmit: event => {
      event.preventDefault();
      event.stopPropagation();
      search();
    },
    children: [/*#__PURE__*/_jsx(_StyledLabel, {
      htmlFor: "search",
      children: /*#__PURE__*/_jsx(Box, {
        paddedRatio: 2,
        children: /*#__PURE__*/_jsx(_StyledStyledIcon, {
          glyph: Icon.GLYPHS.search,
          styledWidth: "20px",
          fillColor: theme.charcoalGrey,
          opacity: 0.5
        })
      })
    }), /*#__PURE__*/_jsx(Text, {
      large: true,
      semiBold: true,
      children: /*#__PURE__*/_jsx(SearchInput, {
        ref: inputBoxRef,
        id: "search",
        type: "text",
        name: "search",
        value: searchText,
        onChange: handleChange,
        onFocus: onFocus,
        onKeyDown: onKeyDown,
        placeholder: placeholder,
        autoComplete: "off",
        autoFocus: autoFocus,
        rounded: true
      })
    }), (alwaysShowClear || hasValue) && /*#__PURE__*/_jsx(Box, {
      position: "absolute",
      topRight: true,
      fullHeight: true,
      styledWidth: "40px",
      children: /*#__PURE__*/_jsx(RawButton, {
        type: "button",
        onClick: clearSearch,
        fullWidth: true,
        fullHeight: true,
        "aria-label": "Clear search",
        children: /*#__PURE__*/_jsx(BoxSpan, {
          centered: true,
          children: /*#__PURE__*/_jsx(StyledIcon, {
            glyph: Icon.GLYPHS.close,
            styledWidth: "15px",
            fillColor: theme.charcoalGrey,
            opacity: 0.5
          })
        })
      })
    })]
  });
};
const SearchBoxWithRef = (props, ref) => /*#__PURE__*/_jsx(SearchBox, _objectSpread(_objectSpread({}, props), {}, {
  inputBoxRef: ref
}));
export default /*#__PURE__*/forwardRef(SearchBoxWithRef);
var _StyledForm = styled("form").withConfig({
  displayName: "SearchBox___StyledForm",
  componentId: "sc-9qbciu-1"
})(["position:relative;width:100%;"]);
var _StyledLabel = styled("label").withConfig({
  displayName: "SearchBox___StyledLabel",
  componentId: "sc-9qbciu-2"
})(["position:absolute;"]);
var _StyledStyledIcon = styled(StyledIcon).withConfig({
  displayName: "SearchBox___StyledStyledIcon",
  componentId: "sc-9qbciu-3"
})(["position:absolute;"]);